<template>
	<div>
		<!-- Name -->
		<div class="flex xs-12 xs-between xs-middle pt1 pb1">
			<div @click="toggleIsExpanded" class="cu2 flex xs-middle">
				<crm-icon
					color="c1"
					:name="isExpanded ? 'chevron-down' : 'chevron-right'"
				/>
				<div class="c6 fw7 fs3">{{ section.name || "---" }}</div>
			</div>
			<popover-section
				class="cu2"
				@section:add_lesson="openAddLessonModal"
				@section:add_section="openAddSectionModal"
				@section:rename="openUpdateSectionModal"
				@section:delete="removeSection"
			/>
		</div>

		<div v-if="isExpanded">
			<!-- Sub sections of this section -->
			<div v-for="subSection in subSections" :key="subSection.id">
				<course-content-menu
					class="ml4"
					:section="subSection"
					:courseId="courseId"
					:selectedLesson="selectedLesson"
					:selectedLocale="selectedLocale"
					v-on="$listeners"
				/>
			</div>

			<!-- Lessons in this section -->
			<div
				v-for="lesson in lessons"
				:key="lesson.id"
				class="ml5 pt1 pb1 cu2 flex xs-12 xs-between"
				:class="{ 'c2 fw6': lesson.id === selectedLesson }"
				@click="
					$emit('lesson:select', { lessonId: lesson.id, sectionId: section.id })
				"
			>
				<div class="pl2 fs3">{{ lesson.name || "---" }}</div>
				<!-- Actions per lesson -->
				<popover-lesson
					class="cu2"
					@lesson:edit="openUpdateLessonModal(lesson)"
					@lesson:delete="removeLesson(lesson)"
				/>
			</div>
		</div>

		<!-- Create lesson modal -->
		<core-modal
			:name="`createLessonModal-${section.id}`"
			:headline="$t('create_lesson')"
		>
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<div class="mb2 fw7">{{ $t("description") }}</div>
				<j-base-input-text class="mb8" v-model="form.description" />
				<div class="mb2 fw7">{{ $t("version") }}</div>
				<j-base-input-text class="mb8" v-model="form.version" />
				<div class="mb2 fw7">{{ $t("estimated_duration") }}</div>
				<input-duration-in-seconds
					class="mb8"
					v-model="form.durationInSeconds"
				/>
				<div class="mb2 fw7">{{ $t("weight") }}</div>
				<j-base-input-number class="mb8" v-model="form.weight" />
				<div class="mb2 fw7">{{ $t("required_ratio_to_validate") }}</div>
				<j-base-input-number class="mb8" v-model="form.passingRatio" />
				<j-button @click="addLessonToSection">{{ $t("create") }}</j-button>
			</div>
		</core-modal>
		<!-- Edit lesson modal -->
		<core-modal
			:name="`updateLessonModal-${section.id}`"
			:headline="$t('edit_lesson')"
		>
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<div class="mb2 fw7">{{ $t("description") }}</div>
				<j-base-input-text class="mb8" v-model="form.description" />
				<div class="mb2 fw7">{{ $t("version") }}</div>
				<j-base-input-text class="mb8" v-model="form.version" />
				<div class="mb2 fw7">{{ $t("estimated_duration") }}</div>
				<input-duration-in-seconds
					class="mb8"
					v-model="form.durationInSeconds"
				/>
				<div class="mb2 fw7">{{ $t("weight") }}</div>
				<j-base-input-number class="mb8" v-model="form.weight" />
				<div class="mb2 fw7">{{ $t("required_ratio_to_validate") }}</div>
				<j-base-input-number class="mb8" v-model="form.passingRatio" />
				<j-button @click="updateLesson">{{ $t("update") }}</j-button>
			</div>
		</core-modal>
		<!-- Create section modal -->
		<core-modal
			:name="`createSectionModal-${section.id}`"
			:headline="$t('add_section')"
		>
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<j-button @click="addSectionToSection">{{ $t("add") }}</j-button>
			</div>
		</core-modal>
		<!-- Edit section modal -->
		<core-modal
			:name="`updateSectionModal-${section.id}`"
			:headline="$t('rename_section')"
		>
			<div class="p4">
				<div class="mb2 fw7">{{ $t("name") }}</div>
				<j-base-input-text class="mb8" v-model="form.name" />
				<j-button @click="updateSection">{{ $t("save") }}</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon"
import JButton from "@/components/JButton"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import CoreModal from "@/modules/core/components/CoreModal"
import PopoverLesson from "./PopoverLesson"
import PopoverSection from "./PopoverSection"
import InputDurationInSeconds from "./InputDurationInSeconds"

import api from "@/api/gql.js"
import queries from "../utils/queries.js"

export default {
	name: "CourseContentMenu",

	components: {
		CrmIcon,
		JButton,
		JBaseInputText,
		CoreModal,
		PopoverLesson,
		PopoverSection,
		InputDurationInSeconds,
		JBaseInputNumber
	},

	props: {
		courseId: String,
		section: Object,
		selectedLesson: String,
		selectedLocale: String
	},

	data() {
		return {
			isExpanded: true,
			form: {
				name: null,
				description: null,
				version: null,
				durationInSeconds: null,
				passingRatio: null,
				weight: null,
				id: null
			}
		}
	},

	computed: {
		subSections() {
			return this.section?.sections || []
		},

		lessons() {
			return this.section?.lessons || []
		}
	},

	methods: {
		toggleIsExpanded() {
			this.isExpanded = !this.isExpanded
		},

		hasLessons(section) {
			return Boolean(section?.lessons?.length)
		},

		openAddLessonModal() {
			this.$modal.show(`createLessonModal-${this.section.id}`)
			this.form = {
				name: null,
				description: null,
				version: null,
				durationInSeconds: null,
				passingRatio: null,
				weight: null,
				id: null
			}
		},

		openUpdateLessonModal(lesson) {
			this.$modal.show(`updateLessonModal-${this.section.id}`)
			this.form = {
				name: lesson?.name,
				description: lesson?.description,
				version: lesson?.version,
				durationInSeconds: lesson?.durationInSeconds,
				passingRatio: lesson?.passingRatio,
				weight: lesson?.weight,
				id: lesson?.id
			}
		},

		openAddSectionModal() {
			this.$modal.show(`createSectionModal-${this.section.id}`)
			this.form.name = null
			this.form.id = null
		},

		openUpdateSectionModal() {
			this.$modal.show(`updateSectionModal-${this.section.id}`)
			this.form.name = this.section?.name
			this.form.id = this.section?.id
		},

		async addLessonToSection() {
			this.isLoading = true
			/*
			 * Add lesson to section
			 */
			let variables = {
				courseId: this.courseId,
				sectionId: this.section.id,
				input: {
					durationInSeconds: this.form?.durationInSeconds,
					version: this?.form?.version,
					passingRatio: this?.form?.passingRatio,
					weight: this?.form?.weight
				}
			}

			let res = await api({ query: queries.addLessonToSection, variables })

			if (res?.data?.addLessonToSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			const createdLessonId = res.data.addLessonToSection.createdStatus?.id
			/*
			 * Upsert name in L10n table
			 */
			if (this.form?.name?.length) {
				variables = {
					l10n: {
						key: `lessons-${createdLessonId}-name`,
						locale: this.selectedLocale,
						value: this.form?.name
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			if (this.form?.description?.length) {
				variables = {
					l10n: {
						key: `lessons-${createdLessonId}-description`,
						locale: this.selectedLocale,
						value: this.form?.description
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			this.isLoading = false
			this.form.name = null
			this.$modal.hide(`createLessonModal-${this.section.id}`)
			this.$emit("lesson:select", createdLessonId)
			this.$emit("refetch")
		},

		async updateLesson() {
			if (!this.form?.id) return
			let variables = {
				courseId: this.courseId,
				sectionId: this.section.id,
				lessonId: this.form.id,
				input: {
					durationInSeconds: this.form?.durationInSeconds,
					passingRatio: this.form?.passingRatio,
					weight: this.form?.weight
				}
			}

			let res = await api({ query: queries.updateLessonOfSection, variables })

			if (res?.data?.updateLessonOfSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			// Upsert name
			if (this.form?.name?.length) {
				variables = {
					l10n: {
						key: `lessons-${this.form.id}-name`,
						locale: this.selectedLocale,
						value: this.form?.name
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			// Upsert description
			if (this.form?.description?.length) {
				variables = {
					l10n: {
						key: `lessons-${this.form.id}-description`,
						locale: this.selectedLocale,
						value: this.form?.description
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
			}
			this.isLoading = false
			this.form.name = null
			this.form.description = null
			this.form.id = null
			this.$modal.hide(`updateLessonModal-${this.section.id}`)
			this.$emit("refetch")
		},

		async removeLesson(lesson) {
			if (lesson?.items?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.this_lesson_is_not_empty")
					}
				})
				return
			}
			/*
			 * Remove lesson from section
			 */
			let variables = {
				courseId: this.courseId,
				sectionId: this.section.id,
				lessonId: lesson.id
			}

			let res = await api({ query: queries.removeLessonFromSection, variables })

			if (res?.data?.removeLessonFromSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			/*
			 * Delete l10n for lesson name and description
			 * TODO: Delete all localizedContent of lesson items
			 */
			variables = {
				key: `lessons-${lesson.id}-name`
			}
			res = await api({ query: queries.deleteAllL10n, variables })
			variables = {
				key: `lessons-${lesson.id}-description`
			}
			res = await api({ query: queries.deleteAllL10n, variables })

			this.$emit("lesson:removed")
		},

		async addSectionToSection() {
			this.isLoading = true
			/*
			 * Add section to section
			 */
			let variables = {
				courseId: this.courseId,
				sectionId: this.section.id
			}

			let res = await api({ query: queries.addSectionToSection, variables })

			if (res?.data?.addSectionToSection?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			const createdSectionId = res.data.addSectionToSection.createdStatus?.id
			if (!createdSectionId) return
			/*
			 * Upsert name in L10n table
			 */
			if (this.form?.name?.length) {
				variables = {
					l10n: {
						key: `sections-${createdSectionId}-name`,
						locale: this.selectedLocale,
						value: this.form?.name
					}
				}

				res = await api({ query: queries.upsertL10n, variables })

				if (res?.data?.upsertL10n?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text: this.$t("errors.general")
						}
					})
					this.isLoading = false
					return
				}
				this.isLoading = false
				this.form.name = null
				this.$modal.hide(`createSectionModal-${this.section.id}`)
				this.$emit("refetch")
			}
		},

		async updateSection() {
			if (!this.form?.id) return
			const variables = {
				l10n: {
					key: `sections-${this.form.id}-name`,
					locale: this.selectedLocale,
					value: this.form?.name
				}
			}

			const res = await api({ query: queries.upsertL10n, variables })

			if (res?.data?.upsertL10n?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			this.isLoading = false
			this.form.name = null
			this.form.description = null
			this.form.id = null
			this.$modal.hide(`updateSectionModal-${this.section.id}`)
			this.$emit("refetch")
		},

		async removeSection() {
			/*
			 * Remove section
			 */
			if (this?.section?.lessons?.length || this.section?.sections?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.this_section_is_not_empty")
					}
				})
				return
			}
			let variables = {
				courseId: this.courseId,
				sectionId: this.section.id
			}

			let res = await api({ query: queries.removeSectionFromCourse, variables })

			if (res?.data?.removeSectionFromCourse?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			/*
			 * Delete L10n
			 */
			variables = {
				key: `sections-${this.section.id}-name`
			}
			res = await api({ query: queries.deleteAllL10n, variables })
			this.$emit("refetch")
		}
	}
}
</script>
